import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Product,
  ProductDevelopmentPhase,
  DevelopmentPhase,
  ProductOperationResult
} from '../types/product';
import { createSyncMiddleware, type WithSyncControls } from './middleware/syncMiddleware';
import { DataMigrationService } from '../services/DataMigrationService';
import type { DataVersion } from '../utils/dataMigration';
import { DEVELOPMENT_CONSTANTS } from '../config/gameConstants';

export interface ProductPipeline {
  concept: Product[];
  development: Product[];
  design: Product[];
  engineering: Product[];
  testing: Product[];
  manufacturing: Product[];
  production: Product[];
  released: Product[];
  discontinued: Product[];
}

export interface ProductStore extends WithSyncControls {
  // State
  products: Product[];
  activeProduct: Product | null;
  pipeline: ProductPipeline;
  developmentPhases: Record<string, ProductDevelopmentPhase>;
  
  // Actions
  createProduct: (product: Product) => void;
  updateProduct: (productId: string, updates: Partial<Product>) => void;
  deleteProduct: (productId: string) => void;
  setActiveProduct: (product: Product | null) => void;
  updatePipeline: (pipeline: Partial<ProductPipeline>) => void;
  
  // Pipeline management
  moveProductToPipeline: (productId: string, status: Product['status']) => void;
  moveProductToNextPhase: (productId: string) => void;
  discontinueProduct: (productId: string, discontinuedDate: { day: number; month: number; year: number }) => void;
  
  // Development phase management
  startDevelopmentPhase: (productId: string, phase: DevelopmentPhase, startDate: { day: number; month: number; year: number }) => void;
  updateDevelopmentPhase: (productId: string, updates: Partial<ProductDevelopmentPhase>) => void;
  completeDevelopmentPhase: (productId: string) => void;
  
  // Bulk operations
  setProducts: (products: Product[]) => void;
  updateMultipleProducts: (updates: Array<{ id: string; updates: Partial<Product> }>) => void;
  
  // Product validation
  validateProduct: (product: Product) => ProductOperationResult;
  
  // Reset
  resetProducts: () => void;
}

const initialPipeline: ProductPipeline = {
  concept: [],
  development: [],
  design: [],
  engineering: [],
  testing: [],
  manufacturing: [],
  production: [],
  released: [],
  discontinued: [],
};

const STATUS_ORDER: Product['status'][] = [
  'concept',
  'development',
  'design',
  'engineering',
  'testing',
  'manufacturing',
  'production',
  'released',
  'discontinued',
];

// Current version of the product store schema
const PRODUCT_STORE_VERSION: DataVersion = { major: 1, minor: 0, patch: 0 };

// Partialize function to select only persistent data
const partializeProductStore = (state: ProductStore) => ({
  // Persist all product data
  products: state.products,
  pipeline: state.pipeline,
  developmentPhases: state.developmentPhases,
  // Exclude activeProduct as it's just UI selection state
  // activeProduct is excluded
});

// Custom sync configuration for product store
const productSyncConfig = {
  routing: {
    rules: [
      // Products array should always go to IndexedDB due to complexity
      {
        match: (path) => path === 'products' || path.startsWith('products.'),
        target: 'indexedDB' as const,
        priority: 100
      },
      // Pipeline data is complex and should be in IndexedDB
      {
        match: (path) => path === 'pipeline' || path.startsWith('pipeline.'),
        target: 'indexedDB' as const,
        priority: 90
      },
      // Development phases are tied to products
      {
        match: (path) => path === 'developmentPhases' || path.startsWith('developmentPhases.'),
        target: 'indexedDB' as const,
        priority: 80
      },
      // Default to IndexedDB for all product data
      {
        match: () => true,
        target: 'indexedDB' as const,
        priority: 0
      }
    ],
    defaultTarget: 'indexedDB' as const,
    sizeThreshold: 50 * 1024 // 50KB threshold
  },
  behavior: {
    debounceDelay: 2000, // 2 second debounce for product updates
    batchSize: 50, // Batch large product arrays
    enableAutoSync: true,
    syncOnReconnect: true,
    maxRetries: 5
  },
  hooks: {
    beforeSync: (data) => {
      // Clean up product data before sync
      if (data.products && Array.isArray(data.products)) {
        // Ensure all products have valid IDs
        data.products = data.products.filter(p => p && p.id);
        
        // Rebuild pipeline from products if needed
        if (!data.pipeline) {
          const pipeline: ProductPipeline = {
            concept: [],
            development: [],
            design: [],
            engineering: [],
            testing: [],
            manufacturing: [],
            production: [],
            released: [],
            discontinued: []
          };
          
          data.products.forEach((product: Product) => {
            if (pipeline[product.status]) {
              pipeline[product.status].push(product);
            }
          });
          
          data.pipeline = pipeline;
        }
      }
      return data;
    },
    afterSync: (result) => {
      if (result.success) {
        // console.debug('[ProductStore] Synced successfully to', result.target);
      }
    },
    onError: (error) => {
      console.error('[ProductStore] Sync error:', error.message);
    },
    onConflict: (conflict) => {
      // Handle conflicts by preferring the newer data
      console.warn('[ProductStore] Sync conflict detected:', conflict);
      const localTime = conflict.local?.lastModified || 0;
      const remoteTime = conflict.remote?.lastModified || 0;
      return localTime > remoteTime ? conflict.local : conflict.remote;
    }
  },
  monitoring: {
    trackMetrics: true,
    reportInterval: 60000, // Report metrics every minute
    metricsCallback: (metrics) => {
      // Monitor for performance issues with large product arrays
      if (metrics.averageSyncTime > 1000) {
        console.warn('[ProductStore] Slow sync performance detected:', metrics.averageSyncTime, 'ms');
      }
    }
  }
};

// Initialize migration service for product store
const migrationService = DataMigrationService.getInstance();

// Register migration schemas
migrationService.registerMigrationSchemas('productStore', [
  {
    version: { major: 1, minor: 0, patch: 0 },
    description: 'Initial product store schema',
    up: async (data) => data,
    down: async (data) => data
  }
]);

export const useProductStore = create<ProductStore>()(
  devtools(
    createSyncMiddleware(
      'productStore',
      productSyncConfig
    )(
      (set, get) => ({
      // Initial state
      products: [],
      activeProduct: null,
      pipeline: initialPipeline,
      developmentPhases: {},

      // Product CRUD actions
      createProduct: (product) =>
        set(
          (state) => {
            const newProducts = [...state.products, product];
            const newPipeline = { ...state.pipeline };
            newPipeline[product.status] = [...newPipeline[product.status], product];
            
            return {
              products: newProducts,
              pipeline: newPipeline,
            };
          },
          false,
          'createProduct'
        ),

      updateProduct: (productId, updates) =>
        set(
          (state) => {
            const oldProduct = state.products.find((p) => p.id === productId);
            if (!oldProduct) return state;

            const updatedProduct = { ...oldProduct, ...updates };
            const newProducts = state.products.map((p) =>
              p.id === productId ? updatedProduct : p
            );

            // Update pipeline if status changed
            const newPipeline = { ...state.pipeline };
            if (updates.status && updates.status !== oldProduct.status) {
              // Remove from old status
              newPipeline[oldProduct.status] = newPipeline[oldProduct.status].filter(
                (p) => p.id !== productId
              );
              // Add to new status
              newPipeline[updates.status] = [...newPipeline[updates.status], updatedProduct];
            } else {
              // Update product in current status
              newPipeline[oldProduct.status] = newPipeline[oldProduct.status].map((p) =>
                p.id === productId ? updatedProduct : p
              );
            }

            return {
              products: newProducts,
              pipeline: newPipeline,
              activeProduct:
                state.activeProduct?.id === productId ? updatedProduct : state.activeProduct,
            };
          },
          false,
          'updateProduct'
        ),

      deleteProduct: (productId) =>
        set(
          (state) => {
            const product = state.products.find((p) => p.id === productId);
            if (!product) return state;

            const newProducts = state.products.filter((p) => p.id !== productId);
            const newPipeline = { ...state.pipeline };
            newPipeline[product.status] = newPipeline[product.status].filter(
              (p) => p.id !== productId
            );

            const newDevelopmentPhases = { ...state.developmentPhases };
            delete newDevelopmentPhases[productId];

            return {
              products: newProducts,
              pipeline: newPipeline,
              developmentPhases: newDevelopmentPhases,
              activeProduct: state.activeProduct?.id === productId ? null : state.activeProduct,
            };
          },
          false,
          'deleteProduct'
        ),

      setActiveProduct: (product) =>
        set(() => ({ activeProduct: product }), false, 'setActiveProduct'),

      updatePipeline: (pipeline) =>
        set(
          (state) => ({
            pipeline: { ...state.pipeline, ...pipeline },
          }),
          false,
          'updatePipeline'
        ),

      // Pipeline management
      moveProductToPipeline: (productId, status) => {
        const { updateProduct } = get();
        updateProduct(productId, { status });
      },

      moveProductToNextPhase: (productId) => {
        const { products, updateProduct } = get();
        const product = products.find((p) => p.id === productId);
        if (!product) return;

        const currentIndex = STATUS_ORDER.indexOf(product.status);
        if (currentIndex < STATUS_ORDER.length - 1) {
          const nextStatus = STATUS_ORDER[currentIndex + 1];
          updateProduct(productId, { status: nextStatus });
        }
      },

      discontinueProduct: (productId, discontinuedDate) => {
        const { products, updateProduct } = get();
        const product = products.find((p) => p.id === productId);
        if (!product) return;
        
        updateProduct(productId, {
          status: 'discontinued',
          metadata: {
            ...product.metadata,
            discontinuedDate,
          },
        });
      },

      // Development phase management
      startDevelopmentPhase: (productId, phase, startDate) =>
        set(
          (state) => {
            const newPhase: ProductDevelopmentPhase = {
              phase,
              startDate,
              estimatedDuration: 30, // Default 30 days
              budget: 100000, // Default budget
              confidence: DEVELOPMENT_CONSTANTS.initialConfidence,
              qualityScore: DEVELOPMENT_CONSTANTS.initialQuality,
              riskEvents: [],
              investmentCount: 0,
            };

            return {
              developmentPhases: {
                ...state.developmentPhases,
                [productId]: newPhase,
              },
            };
          },
          false,
          'startDevelopmentPhase'
        ),

      updateDevelopmentPhase: (productId, updates) =>
        set(
          (state) => {
            const currentPhase = state.developmentPhases[productId];
            if (!currentPhase) return state;

            return {
              developmentPhases: {
                ...state.developmentPhases,
                [productId]: { ...currentPhase, ...updates },
              },
            };
          },
          false,
          'updateDevelopmentPhase'
        ),

      completeDevelopmentPhase: (productId) => {
        const { developmentPhases, moveProductToNextPhase } = get();
        const phase = developmentPhases[productId];
        if (!phase) return;

        set(
          (state) => {
            const newPhases = { ...state.developmentPhases };
            delete newPhases[productId];
            return { developmentPhases: newPhases };
          },
          false,
          'completeDevelopmentPhase'
        );

        moveProductToNextPhase(productId);
      },

      // Bulk operations
      setProducts: (products) =>
        set(
          () => {
            const newPipeline: ProductPipeline = {
              concept: [],
              development: [],
              design: [],
              engineering: [],
              testing: [],
              manufacturing: [],
              production: [],
              released: [],
              discontinued: [],
            };

            products.forEach((product) => {
              newPipeline[product.status].push(product);
            });

            return {
              products,
              pipeline: newPipeline,
            };
          },
          false,
          'setProducts'
        ),

      updateMultipleProducts: (updates) =>
        set(
          (state) => {
            const updateMap = new Map(updates.map((u) => [u.id, u.updates]));
            const newProducts = state.products.map((product) => {
              const productUpdates = updateMap.get(product.id);
              return productUpdates ? { ...product, ...productUpdates } : product;
            });

            // Rebuild pipeline
            const newPipeline: ProductPipeline = {
              concept: [],
              development: [],
              design: [],
              engineering: [],
              testing: [],
              manufacturing: [],
              production: [],
              released: [],
              discontinued: [],
            };

            newProducts.forEach((product) => {
              newPipeline[product.status].push(product);
            });

            return {
              products: newProducts,
              pipeline: newPipeline,
            };
          },
          false,
          'updateMultipleProducts'
        ),

      // Product validation
      validateProduct: (product) => {
        // Basic validation logic
        if (!product.name || product.name.trim().length === 0) {
          return {
            success: false,
            error: 'Product name is required',
          };
        }

        if (product.price <= 0) {
          return {
            success: false,
            error: 'Product price must be greater than 0',
          };
        }

        if (product.productionCost < 0) {
          return {
            success: false,
            error: 'Production cost cannot be negative',
          };
        }

        if (product.productionCost >= product.price) {
          return {
            success: false,
            error: 'Production cost must be less than price',
          };
        }

        return {
          success: true,
          data: product,
        };
      },

      // Reset
      resetProducts: () =>
        set(
          {
            products: [],
            activeProduct: null,
            pipeline: initialPipeline,
            developmentPhases: {},
          },
          false,
          'resetProducts'
        ),
    })
    ),
    {
      name: 'product-store',
      serialize: {
        options: {
          map: true,
          set: true,
          date: true,
          error: true,
          regex: true,
          undefined: true,
          nan: true,
          infinity: true,
          symbol: false
        }
      }
    }
  )
);

// Helper to persist product data
export const persistProductData = () => {
  const state = useProductStore.getState();
  const persistentData = partializeProductStore(state);
  return persistentData;
};

// Helper to load persisted product state
export const loadPersistedProductState = async () => {
  try {
    // Trigger migration if needed
    await migrationService.migrate('productStore', PRODUCT_STORE_VERSION, {
      createBackup: true,
      validateAfter: true,
      progressive: true // Use progressive migration for large product arrays
    });
    
    // Load persisted data
    const persistedData = await migrationService.getCurrentVersion('productStore');
    return persistedData;
  } catch (error) {
    console.error('[ProductStore] Failed to load persisted state:', error);
    return null;
  }
};

// Helper to export products data
export const exportProductsData = async (format: 'json' | 'csv' = 'json') => {
  try {
    const blob = await migrationService.exportData('productStore', format);
    return blob;
  } catch (error) {
    console.error('[ProductStore] Failed to export data:', error);
    return null;
  }
};

// Helper to import products data
export const importProductsData = async (file: File) => {
  try {
    const result = await migrationService.importData('productStore', file, {
      validate: true,
      createBackup: true
    });
    
    if (result.success) {
      // Reload the store with imported data
      const state = useProductStore.getState();
      const products = await loadPersistedProductState();
      if (products) {
        state.setProducts(products as Product[]);
      }
    }
    
    return result;
  } catch (error) {
    console.error('[ProductStore] Failed to import data:', error);
    return null;
  }
};

// Helper to handle batch relationships (for managing product-batch relationships)
export const syncProductBatchRelationships = async () => {
  const state = useProductStore.getState();
  const products = state.products;
  
  // This would integrate with a batch store if it exists
  // For now, just return the product count
  
  return products.length;
};

// Selectors
export const selectProducts = (state: ProductStore) => state.products;
export const selectActiveProduct = (state: ProductStore) => state.activeProduct;
export const selectPipeline = (state: ProductStore) => state.pipeline;
export const selectDevelopmentPhases = (state: ProductStore) => state.developmentPhases;

// Computed selectors
export const selectProductById = (productId: string) => (state: ProductStore) =>
  state.products.find((p) => p.id === productId);

export const selectProductsByStatus = (status: Product['status']) => (state: ProductStore) =>
  state.pipeline[status];

export const selectProductCount = (state: ProductStore) => state.products.length;

export const selectActiveProductCount = (state: ProductStore) =>
  state.products.filter((p) => p.status === 'production').length;

export const selectProductsInDevelopment = (state: ProductStore) =>
  state.products.filter((p) =>
    ['development', 'design', 'engineering', 'testing', 'manufacturing'].includes(p.status)
  );

export const selectProductDevelopmentPhase = (productId: string) => (state: ProductStore) =>
  state.developmentPhases[productId];

export const selectProductsByCompany = (companyId: string) => (state: ProductStore) =>
  state.products.filter((p) => p.metadata.companyId === companyId);

export const selectProductRevenue = (state: ProductStore) =>
  state.products.reduce((total, product) => total + product.salesMetrics.revenue, 0);

export const selectProductMarketShare = (state: ProductStore) =>
  state.products.reduce((total, product) => total + product.salesMetrics.marketShare, 0);

export const selectTopSellingProducts = (limit: number = 5) => (state: ProductStore) =>
  [...state.products]
    .sort((a, b) => b.salesMetrics.unitsSold - a.salesMetrics.unitsSold)
    .slice(0, limit);